import React, { useState, useEffect } from 'react';
import { Gift, Sparkles, ChevronRight, Star } from 'lucide-react';

const ChristmasWidgets = ({ config = {} }) => {
  const defaultConfig = {
    preset: 'design1',
    title: 'Holiday Sale',
    subtitle: 'Up to 70% Off Everything',
    buttonText: 'Shop Now',
    countdownDate: '2026-12-25T00:00:00',
    showSnow: true,
    showStars: true,
    customStyles: {
      background: 'bg-gradient-to-br from-red-900 via-red-700 to-green-900',
      textColor: '#ffffff',
      accentColor: '#fbbf24', // yellow-400
      buttonBg: '#ffffff',
      buttonText: '#991b1b', // red-900
    },
  };

  const finalConfig = { ...defaultConfig, ...config };
  const {
    preset,
    title,
    subtitle,
    buttonText,
    countdownDate,
    showSnow,
    showStars,
    customStyles,
  } = finalConfig;

  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [activeWidget, setActiveWidget] = useState(0);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const target = new Date(countdownDate);
      const now = new Date();
      const diff = target - now;

      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / (1000 * 60)) % 60),
          seconds: Math.floor((diff / 1000) % 60),
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [countdownDate]);

  // ── DESIGN 1 ───────────────────────────────────────────────────────
  const Design1 = () => (
    <div className={`relative h-96 ${customStyles.background} rounded-3xl overflow-hidden shadow-2xl`}>
      {showSnow && (
        <div className="absolute inset-0 opacity-20">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white rounded-full animate-pulse"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 3}s`,
              }}
            />
          ))}
        </div>
      )}

      <div className="relative z-10 h-full flex flex-col items-center justify-center p-8 text-white">
        <div className="flex items-center gap-3 mb-4 animate-bounce">
          <Gift className="w-12 h-12" />
          <Sparkles className="w-8 h-8" style={{ color: customStyles.accentColor }} />
        </div>

        <h1
          className="text-5xl font-bold mb-2 text-center bg-clip-text text-transparent"
          style={{
            backgroundImage: `linear-gradient(to right, ${customStyles.textColor}, ${customStyles.accentColor})`,
          }}
        >
          {title}
        </h1>
        <p className="text-xl mb-8 opacity-90">{subtitle}</p>

        <div className="grid grid-cols-4 gap-4 mb-8">
          {Object.entries(timeLeft).map(([unit, value]) => (
            <div
              key={unit}
              className="bg-white/20 backdrop-blur-md rounded-xl p-4 border border-white/30"
            >
              <div className="text-4xl font-bold">{String(value).padStart(2, '0')}</div>
              <div className="text-xs uppercase mt-1 opacity-80">{unit}</div>
            </div>
          ))}
        </div>

        <button
          className="group px-8 py-4 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-lg flex items-center gap-2"
          style={{
            backgroundColor: customStyles.buttonBg,
            color: customStyles.buttonText,
          }}
        >
          {buttonText}
          <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  );

  // ── DESIGN 2 ───────────────────────────────────────────────────────
  const Design2 = () => (
    <div className={`relative w-full h-full ${customStyles.background} rounded-3xl overflow-hidden shadow-2xl`}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.1),transparent)]" />

      {showSnow && (
        <div className="absolute top-0 left-0 w-full h-full">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute text-6xl opacity-10"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                transform: `rotate(${Math.random() * 360}deg)`,
                animation: `float ${5 + Math.random() * 5}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            >
              ❄️
            </div>
          ))}
        </div>
      )}

      <div className="relative z-10 h-full flex flex-col items-center justify-center p-8 text-white">
        <div className="text-sm uppercase tracking-widest mb-2 opacity-90 font-semibold">
          Limited Time
        </div>
        <h1 className="text-6xl font-bold mb-4 text-center">
          Christmas <span style={{ color: customStyles.accentColor }}>Countdown</span>
        </h1>

        <div className="flex gap-3 mb-8">
          {Object.entries(timeLeft).map(([unit, value]) => (
            <div key={unit} className="relative">
              <div
                className="rounded-2xl p-6 border-2 shadow-xl min-w-[90px]"
                style={{
                  background: `linear-gradient(to bottom, ${customStyles.accentColor}40, ${customStyles.accentColor}80)`,
                  borderColor: customStyles.accentColor,
                }}
              >
                <div className="text-5xl font-black" style={{ color: customStyles.accentColor }}>
                  {String(value).padStart(2, '0')}
                </div>
                <div className="text-xs uppercase mt-2 font-bold" style={{ color: customStyles.accentColor }}>
                  {unit}
                </div>
              </div>
              <div
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full animate-ping"
                style={{ backgroundColor: customStyles.accentColor }}
              />
            </div>
          ))}
        </div>

        <p className="text-2xl mb-6 text-center font-light">🎁 Unwrap Amazing Deals 🎁</p>

        <button
          className="px-10 py-4 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-2xl"
          style={{
            background: `linear-gradient(to right, ${customStyles.accentColor}, #d97706)`,
            color: customStyles.buttonText,
          }}
        >
          {buttonText}
        </button>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(10deg); }
        }
      `}</style>
    </div>
  );

  // ── DESIGN 3 ───────────────────────────────────────────────────────
  const Design3 = () => (
    <div className={`relative h-full w-full ${customStyles.background} rounded-3xl overflow-hidden shadow-2xl`}>
      {showStars && (
        <div className="absolute inset-0">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animation: `twinkle ${1 + Math.random() * 2}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      )}

      <div className="relative z-10 h-full flex items-center justify-between p-12">
        <div className="flex-1">
          <div
            className="inline-block px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse"
            style={{ backgroundColor: customStyles.accentColor, color: '#111827' }}
          >
            🔥 FLASH SALE
          </div>
          <h1 className="text-6xl font-black mb-4 leading-tight">
            <span style={{ color: customStyles.textColor }}>Merry</span>
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: `linear-gradient(to right, ${customStyles.accentColor}, #10b981, ${customStyles.accentColor})`,
              }}
            >
              Savings
            </span>
          </h1>
          <p className="text-xl mb-8 max-w-md" style={{ color: customStyles.textColor + 'cc' }}>
            Biggest discounts of the year end in:
          </p>

          <div className="flex gap-2 mb-8">
            {Object.entries(timeLeft).map(([unit, value]) => (
              <div key={unit} className="text-center">
                <div
                  className="backdrop-blur-xl rounded-xl p-3 border min-w-[70px] shadow-xl"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderColor: 'rgba(255,255,255,0.2)',
                  }}
                >
                  <div className="text-3xl font-bold" style={{ color: customStyles.textColor }}>
                    {String(value).padStart(2, '0')}
                  </div>
                </div>
                <div className="text-xs uppercase mt-2 font-semibold opacity-80">
                  {unit}
                </div>
              </div>
            ))}
          </div>

          <button
            className="group px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-xl flex items-center gap-3 transform hover:scale-105"
            style={{
              background: `linear-gradient(to right, #10b981, #059669)`,
              color: '#ffffff',
            }}
          >
            <Gift className="w-5 h-5" />
            {buttonText}
            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        <div className="hidden lg:block text-9xl animate-bounce">🎄</div>
      </div>

      <style jsx>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0; transform: scale(0); }
          50% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );

  const widgets = [Design1, Design2, Design3];
  const CurrentWidget = widgets[activeWidget];

  return (
    <div className="min-h-screen bg-slate-950 p-8">
      <div className="max-w-9xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-4">Christmas Marketing Hero</h2>
          <div className="flex gap-3 justify-center">
            {widgets.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveWidget(idx)}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  activeWidget === idx
                    ? 'bg-red-600 text-white shadow-lg'
                    : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
                }`}
              >
                Design {idx + 1}
              </button>
            ))}
          </div>
        </div>

        <CurrentWidget />
      </div>
    </div>
  );
};

export default ChristmasWidgets;